import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faInfoCircle, faClock, faSprayCan, faHome,
  faBroom, faPlus, faMinus, faCircleNotch, faUndo
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';

const goldColor = '#c5a059';

const CleaningCalculator = ({ onResultChange }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [serverResult, setServerResult] = useState(null);
  const [finalOrderInfo, setFinalOrderInfo] = useState(null);
  const [address, setAddress] = useState('');
  const [data, setData] = useState({
    mainMode: 'general_group',
    type: 'standard',
    sqm: 50,
    hasWindows: false, 
    windowSqm: 0,
    hasShelves: false,
    hasKitchen: false,
    kitchenItems: { fridge: false, oven: false, hood: false, dishwasher: false },
    cleanersCount: 1,
    hours: 3,
    dryItems: { sofa: 0, chair: 0, mattress: 0, carpet: 0 },
    tools: { vacuum: true, steam: false, materials: true }
  });

const calculateOnServer = async () => {
  setLoading(true);
  try {
    const calcPayload = { 
      category: 'CLEANING', 
      calculatorId: 4, 
      ...data 
    };
    
    const response = await axios.post(`${API_BASE}/api/calculators/calculate`, calcPayload);

    if (response.data.success) {
      const res = response.data;
      const finalPrice = res.totalPrice || res.total || 0;

      // --- 📝 ԼԻԱՐԺԵՔ ՄԵՍԻՋԻ ԿՈՆՍՏՐՈՒԿՏՈՐ ---
      let detailsArray = [];

      // 1. Մաքրման տեսակը
      const modeNames = {
        general_group: 'Հիմնական',
        partial: 'Մասնակի',
        hourly: 'Ժամավճարով',
        dry_cleaning: 'Քիմմաքրում'
      };
      const typeNames = {
        standard: 'Ստանդարտ',
        general: 'Գլխավոր',
        post_construction: 'Վերանորոգումից հետո'
      };

      let mainInfo = `✨ ${modeNames[data.mainMode] || ''}`;
      if (data.mainMode === 'general_group') mainInfo += ` (${typeNames[data.type] || ''})`;
      detailsArray.push(`${mainInfo}: ${data.sqm}քմ`);

      // 2. Լրացուցիչ ծառայություններ
      if (data.hasWindows) detailsArray.push(`🪟 Պատուհան: ${data.windowSqm}քմ`);
      if (data.hasShelves) detailsArray.push(`📦 Դարակների ներսը`);
      
      // 3. Խոհանոցային տեխնիկա
      if (data.hasKitchen) {
        const kitchenTech = Object.entries(data.kitchenItems)
          .filter(([_, checked]) => checked)
          .map(([key, _]) => t(`cleaning_calc.${key}`))
          .join(', ');
        if (kitchenTech) detailsArray.push(`🍳 Տեխնիկա: ${kitchenTech}`);
      }

      // 4. Քիմմաքրում
      if (data.mainMode === 'dry_cleaning' || Object.values(data.dryItems).some(v => v > 0)) {
        const dryItemsArr = [];
        if (data.dryItems.sofa) dryItemsArr.push(`Բազմոց x${data.dryItems.sofa}`);
        if (data.dryItems.chair) dryItemsArr.push(`Աթոռ x${data.dryItems.chair}`);
        if (data.dryItems.mattress) dryItemsArr.push(`Ներքնակ x${data.dryItems.mattress}`);
        if (data.dryItems.carpet) dryItemsArr.push(`Գորգ ${data.dryItems.carpet}քմ`);
        if (dryItemsArr.length > 0) detailsArray.push(`✨ Քիմ: ${dryItemsArr.join(', ')}`);
      }

      // 5. Գործիքներ և նյութեր
      const toolLabels = [];
      if (data.tools.vacuum) toolLabels.push('Փոշեկուլ');
      if (data.tools.steam) toolLabels.push('Գոլորշու սարք');
      if (data.tools.materials) toolLabels.push('Մաքրող նյութեր');
      if (toolLabels.length > 0) detailsArray.push(`🛠 Գործիքներ: ${toolLabels.join(', ')}`);

      // Միացնում ենք ամբողջը մեկ տողի մեջ
      const fullMsg = detailsArray.join(' | ');

      // ---------------------------------------

      const orderData = {
        total_price: Number(finalPrice),
        totalPrice: Number(finalPrice),
        other_expenses: Number(res.business?.otherExpenses || 0), 
        wCount: Number(res.business?.workerCount || data.cleanersCount || 1),
        worker_count: Number(res.business?.workerCount || data.cleanersCount || 1),
        category_name: 'CLEANING',
        service_name: 'Մաքրման աշխատանքներ',
        message: fullMsg,
        details: fullMsg,
        location: address || '', 
        ...data 
      };

      setServerResult({ ...res, total: finalPrice, wCount: orderData.wCount });
      setFinalOrderInfo(orderData);
      setStep(4);

      if (onResultChange) {
        onResultChange(finalPrice, fullMsg, orderData);
      }
    }
  } catch (error) {
    console.error("🛑 Calc Error:", error);
    alert("Հաշվարկի սխալ");
  } finally {
    setLoading(false);
  }
};

  return (
    <div style={s.container}>
      {step < 4 && (
        <div style={s.progressRow}>
          {[1, 2, 3].map(i => (
            <div key={i} style={{ ...s.dot, background: step >= i ? goldColor : '#e2e8f0' }} />
          ))}
        </div>
      )}

      {step === 1 && (
        <div style={s.stepBox}>
          <h3 style={s.stepTitle}>{t('cleaning_calc.step_1_title')}</h3>
          <div style={s.tabsGrid}>
            <button style={data.mainMode === 'general_group' ? s.activeTab : s.tab} onClick={() => setData({ ...data, mainMode: 'general_group' })}>
              <FontAwesomeIcon icon={faHome} /><br />{t('cleaning_calc.general')}
            </button>
            <button style={data.mainMode === 'partial' ? s.activeTab : s.tab} onClick={() => setData({ ...data, mainMode: 'partial' })}>
              <FontAwesomeIcon icon={faBroom} /><br />{t('cleaning_calc.partial')}
            </button>
            <button style={data.mainMode === 'hourly' ? s.activeTab : s.tab} onClick={() => setData({ ...data, mainMode: 'hourly' })}>
              <FontAwesomeIcon icon={faClock} /><br />{t('cleaning_calc.hourly')}
            </button>
            <button style={data.mainMode === 'dry_cleaning' ? s.activeTab : s.tab} onClick={() => setData({ ...data, mainMode: 'dry_cleaning' })}>
              <FontAwesomeIcon icon={faSprayCan} /><br />{t('cleaning_calc.dry_cleaning')}
            </button>
          </div>

          <div style={{ marginTop: 20 }}>
            {data.mainMode === 'general_group' && (
              <div style={s.tabsColumn}>
                <div style={s.subTypeGrid}>
                  <button style={data.type === 'standard' ? s.activeSub : s.subTab} onClick={() => setData({ ...data, type: 'standard' })}>{t('cleaning_calc.standard')}</button>
                  <button style={data.type === 'general' ? s.activeSub : s.subTab} onClick={() => setData({ ...data, type: 'general' })}>{t('cleaning_calc.general_type')}</button>
                  <button style={data.type === 'post_construction' ? s.activeSub : s.subTab} onClick={() => setData({ ...data, type: 'post_construction' })}>{t('cleaning_calc.renovation')}</button>
                </div>
                <input type="range" min="20" max="400" value={data.sqm} onChange={e => setData({ ...data, sqm: parseInt(e.target.value) })} style={s.range} />
                <div style={s.rangeVal}>{data.sqm} {t('cleaning_calc.sqm')}</div>
              </div>
            )}

            {data.mainMode === 'hourly' && (
              <div style={s.tabsColumn}>
                <div style={s.counterRow}>
                  <button style={s.countBtn} onClick={() => setData({ ...data, cleanersCount: Math.max(1, data.cleanersCount - 1) })}><FontAwesomeIcon icon={faMinus} /></button>
                  <span style={s.countNum}>{data.cleanersCount} {t('cleaning_calc.people')}</span>
                  <button style={s.countBtn} onClick={() => setData({ ...data, cleanersCount: data.cleanersCount + 1 })}><FontAwesomeIcon icon={faPlus} /></button>
                </div>
                <input type="range" min="2" max="12" value={data.hours} onChange={e => setData({ ...data, hours: parseInt(e.target.value) })} style={s.range} />
                <div style={s.rangeVal}>{data.hours} {t('cleaning_calc.hours')}</div>
              </div>
            )}

            {data.mainMode === 'dry_cleaning' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                {[
                  { id: 'sofa', label: '🛋️ Բազմոց', unit: 'տեղ' },
                  { id: 'chair', label: '🪑 Աթոռ', unit: 'հատ' },
                  { id: 'mattress', label: '🛏️ Ներքնակ', unit: 'հատ' },
                  { id: 'carpet', label: '🧶 Գորգ', unit: 'քմ' }
                ].map(item => (
                  <div key={item.id} style={s.dryItemRow}>
                    <span style={s.dryLabel}>{item.label}</span>
                    <div style={s.dryControls}>
                      <button style={s.dryBtn} onClick={() => setData({
                        ...data,
                        dryItems: { ...data.dryItems, [item.id]: Math.max(0, (data.dryItems[item.id] || 0) - 1) }
                      })}>-</button>
                      <span style={s.dryNum}>{data.dryItems[item.id] || 0} {item.unit}</span>
                      <button style={s.dryBtn} onClick={() => setData({
                        ...data,
                        dryItems: { ...data.dryItems, [item.id]: (data.dryItems[item.id] || 0) + 1 }
                      })}>+</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button style={s.nextBtn} onClick={() => setStep(2)}>{t('cleaning_calc.continue')}</button>
        </div>
      )}

      {step === 2 && (
        <div style={s.stepBox}>
          <h3 style={s.stepTitle}>{t('cleaning_calc.step_2_title')}</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={s.checkRow}><span>🪟 {t('cleaning_calc.windows')} ({t('cleaning_calc.sqm')})</span><input type="checkbox" checked={data.hasWindows} onChange={e => setData({ ...data, hasWindows: e.target.checked })} /></div>
            {data.hasWindows && <input type="number" placeholder={t('cleaning_calc.sqm')} style={s.input} value={data.windowSqm || ""} onChange={e => setData({ ...data, windowSqm: parseInt(e.target.value) || 0 })} />}
            <div style={s.checkRow}><span>📦 {t('cleaning_calc.shelves')}</span><input type="checkbox" checked={data.hasShelves} onChange={e => setData({ ...data, hasShelves: e.target.checked })} /></div>
            <div style={s.checkRow}><span>🍳 {t('cleaning_calc.kitchen_tech')}</span><input type="checkbox" checked={data.hasKitchen} onChange={e => setData({ ...data, hasKitchen: e.target.checked })} /></div>
            {data.hasKitchen && (
              <div style={s.grid2}>
                {Object.keys(data.kitchenItems).map(k => (
                  <label key={k} style={s.miniCheck}>
                    <input type="checkbox" checked={data.kitchenItems[k]} onChange={e => setData({ ...data, kitchenItems: { ...data.kitchenItems, [k]: e.target.checked } })} /> 
                    {t(`cleaning_calc.${k}`)}
                  </label>
                ))}
              </div>
            )}
          </div>
          <div style={s.btnRow}>
            <button style={s.backBtn} onClick={() => setStep(1)}>{t('cleaning_calc.back')}</button>
            <button style={s.nextBtn} onClick={() => setStep(3)}>{t('cleaning_calc.next')}</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div style={s.stepBox}>
          <h3 style={s.stepTitle}>{t('cleaning_calc.step_3_title')}</h3>
          <div style={s.checkList}>
            <label style={s.listItem}><input type="checkbox" checked={data.tools.vacuum} onChange={e => setData({ ...data, tools: { ...data.tools, vacuum: e.target.checked } })} /> 🌪️ {t('cleaning_calc.vacuum')}</label>
            <label style={s.listItem}><input type="checkbox" checked={data.tools.steam} onChange={e => setData({ ...data, tools: { ...data.tools, steam: e.target.checked } })} /> 💨 {t('cleaning_calc.steam')}</label>
            <label style={s.listItem}><input type="checkbox" checked={data.tools.materials} onChange={e => setData({ ...data, tools: { ...data.tools, materials: e.target.checked } })} /> 🧪 {t('cleaning_calc.materials')}</label>
          </div>
          <div style={s.btnRow}>
            <button style={s.backBtn} onClick={() => setStep(2)}>{t('cleaning_calc.back')}</button>
            <button style={s.nextBtn} onClick={calculateOnServer} disabled={loading}>
              {loading ? <FontAwesomeIcon icon={faCircleNotch} spin /> : t('cleaning_calc.calculate')}
            </button>
          </div>
        </div>
      )}

      {step === 4 && serverResult && finalOrderInfo && (
        <div style={s.successCard}>
          <div style={s.receiptCard}>
            <h3 style={s.successTitle}>📊 {t('cleaning_calc.result_title')}</h3>
            <div style={s.orderItemsList}>
              {/* ✅ ՖԻՔՍՎԱԾ Split Error-ը ?. օգտագործելով */}
              {finalOrderInfo?.details?.split('|').map((item, index) => {
                const text = item.trim();
                if (!text) return null;
                let icon = "•";
                if (text.includes(t('cleaning_calc.sqm'))) icon = "📏";
                if (text.includes(t('cleaning_calc.windows'))) icon = "🪟";
                if (text.includes(t('cleaning_calc.kitchen_tech'))) icon = "🍳";

                return (
                  <div key={index} style={s.orderItemRow}>
                    <span style={{ marginRight: '10px', fontSize: '16px' }}>{icon}</span>
                    <span style={s.itemText}>{text}</span>
                  </div>
                );
              })}
            </div>
            <div style={s.divider} />
            <div style={s.finalRow}>
              <span style={s.finalLabel}>{t('cleaning_calc.total_price')}.</span>
              <span style={s.finalPrice}>{serverResult.total.toLocaleString()} ֏</span>
            </div>
            <div style={s.workerEstimate}>
              <span style={{ fontSize: '18px' }}>👥</span>
              <div>
                <div style={s.workerTitle}>{t('cleaning_calc.worker_count')}</div>
                <div style={s.workerSub}>{serverResult.wCount} {t('cleaning_calc.people')}</div>
              </div>
            </div>
          </div>
          <button style={s.editBtn} onClick={() => setStep(1)}>🔄 {t('cleaning_calc.edit_data')}</button>
        </div>
      )}
    </div>
  );
};

const s = {
  container: { width: '100%', maxWidth: '600px', margin: '0 auto', padding: '10px' },
  progressRow: { display: 'flex', gap: 6, marginBottom: 20 },
  dot: { flex: 1, height: 6, borderRadius: 10 },
  stepBox: { background: '#fff', padding: 20, borderRadius: 25, boxShadow: '0 10px 30px rgba(0,0,0,0.05)' },
  stepTitle: { textAlign: 'center', marginBottom: 20, fontSize: 17, fontWeight: 'bold' },
  tabsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  tabsColumn: { display: 'flex', flexDirection: 'column', gap: 10 },
  subTypeGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5 },
  tab: { padding: 12, borderRadius: 15, border: '1px solid #eee', background: '#fff', fontSize: 11, cursor: 'pointer' },
  activeTab: { padding: 12, borderRadius: 15, background: '#1e293b', color: '#fff', border: 'none', fontSize: 11 },
  subTab: { padding: 8, borderRadius: 10, border: '1px solid #eee', fontSize: 10, cursor: 'pointer' },
  activeSub: { padding: 8, borderRadius: 10, background: goldColor, color: '#fff', border: 'none', fontSize: 10 },
  range: { width: '100%', accentColor: goldColor, margin: '15px 0' },
  rangeVal: { textAlign: 'center', fontSize: 24, fontWeight: 'bold', color: goldColor },
  nextBtn: { width: '100%', padding: 16, background: '#1e293b', color: '#fff', borderRadius: 16, border: 'none', cursor: 'pointer', fontWeight: 'bold' },
  backBtn: { width: '100%', padding: 16, background: '#f1f5f9', color: '#666', borderRadius: 16, border: 'none' },
  btnRow: { display: 'flex', gap: 10, marginTop: 20 },
  counterRow: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 20, marginBottom: 10 },
  countBtn: { width: 40, height: 40, borderRadius: '50%', border: '1px solid #eee', cursor: 'pointer', background: '#fff' },
  countNum: { fontSize: 18, fontWeight: 'bold' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 },
  checkRow: { display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid #f1f5f9' },
  input: { padding: 12, borderRadius: 10, border: '1px solid #eee', width: '100%', boxSizing: 'border-box' },
  miniCheck: { background: '#f8fafc', padding: 8, borderRadius: 10, fontSize: 10, display: 'flex', gap: 5, alignItems: 'center' },
  checkList: { display: 'flex', flexDirection: 'column', gap: 8 },
  listItem: { display: 'flex', gap: 10, background: '#f8fafc', padding: 12, borderRadius: 12, alignItems: 'center' },
  successCard: { textAlign: 'center' },
  receiptCard: { background: '#ffffff', borderRadius: '20px', padding: '20px', boxShadow: '0 10px 25px rgba(0,0,0,0.05)', border: '1px solid #edf2f7', textAlign: 'left' },
  successTitle: { fontSize: '18px', fontWeight: 'bold', textAlign: 'center', color: '#2d3748', marginBottom: '10px' },
  orderItemsList: { marginTop: '15px', display: 'flex', flexDirection: 'column', gap: '8px' },
  orderItemRow: { display: 'flex', alignItems: 'center', background: '#f7fafc', padding: '10px 15px', borderRadius: '12px', border: '1px solid #f1f5f9' },
  itemText: { fontSize: '14px', color: '#4a5568', fontWeight: '500' },
  divider: { height: '1px', background: 'linear-gradient(to right, transparent, #e2e8f0, transparent)', margin: '20px 0' },
  finalRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 5px' },
  finalLabel: { fontSize: '13px', color: '#718096', fontWeight: 'bold' },
  finalPrice: { fontSize: '22px', color: goldColor, fontWeight: '800' },
  workerEstimate: { marginTop: '20px', display: 'flex', alignItems: 'center', gap: '12px', background: '#f0f5ff', padding: '12px', borderRadius: '15px', border: '1px solid #dbeafe' },
  workerTitle: { fontSize: '12px', color: '#64748b' },
  workerSub: { fontSize: '15px', fontWeight: 'bold', color: '#1e3a8a' },
  editBtn: { width: '100%', marginTop: '15px', padding: '12px', borderRadius: '12px', border: 'none', background: '#edf2f7', color: '#4a5568', fontWeight: '600', cursor: 'pointer' },
  dryItemRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px', background: '#f8fafc', borderRadius: '12px' },
  dryLabel: { fontSize: '14px', fontWeight: '500' },
  dryControls: { display: 'flex', alignItems: 'center', gap: '10px' },
  dryBtn: { width: '30px', height: '30px', borderRadius: '50%', border: '1px solid #ddd', background: '#fff', cursor: 'pointer' },
  dryNum: { fontSize: '14px', minWidth: '50px', textAlign: 'center' }
};

export default CleaningCalculator;